<template>
    <div>
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0"></h6>
                </div>
            </div>
        </base-header>
        <div class="container-fluid mt--7">
            <div class="card">
                <div class="card-header mt--2" @keyup.enter="getData">
                    <div class="row align-items-center mb-3">
                        <div class="col">
                            <h3 class="mb-0">{{ tt('mcr_avg_duration') }}</h3>
                        </div>
                        <div class="col-auto">
                            <base-button size="sm" type="default" @click="downloadExport" :disabled="onExport">
                                <span v-if="onExport"><i class="fas fa-spinner fa-spin"></i></span>
                                <span v-else>{{ tt('export_as_excel') }}</span>
                            </base-button>
                            <base-button size="sm" type="primary" @click="getData" :disabled="onLoad">{{ tt('search') }}</base-button>
                        </div>
                    </div>
                    <div class="row align-items-center no-gutters" style="gap: 8px;">
                        <div class="col-auto">
                            <select v-model="filter.view_by" @change="onChangeViewBy"
                                class="form-control form-control-sm" style="width: 160px;">
                                <option value="per-mcr">{{ tt('per_mcr') }}</option>
                                <option value="per-company">{{ tt('per_company') }}</option>
                                <option value="per-jabatan">{{ tt('per_jabatan') }}</option>
                            </select>
                        </div>
                        <!-- Filter Per MCR -->
                        <template v-if="filter.view_by == 'per-mcr'">
                            <div class="col-auto">
                                <el-input v-model="filter.mcr_number" :placeholder="tt('mcr_number')" size="small"
                                    clearable style="width: 160px;"></el-input>
                            </div>
                        </template>
                        <!-- Filter Per Company -->
                        <template v-if="filter.view_by == 'per-company' || filter.view_by == 'per-jabatan'">
                            <div class="col-auto" style="width: 160px;">
                                <el-select v-model="filter.company_code" :placeholder="tt('company')" size="small"
                                    clearable filterable remote :remote-method="searchCompany" :loading="companyLoading"
                                    style="width: 100%;">
                                    <el-option v-for="c in companyList" :key="c.company_code" :value="c.company_code"
                                        :label="c.company_code + ' - ' + c.company_description"></el-option>
                                </el-select>
                            </div>
                        </template>
                        <!-- Filter Per Jabatan -->
                        <template v-if="filter.view_by == 'per-jabatan'">
                            <div class="col-auto">
                                <el-input v-model="filter.role_name" :placeholder="tt('role_name')" size="small"
                                    clearable style="width: 160px;"></el-input>
                            </div>
                        </template>
                        <div class="col-auto">
                            <el-date-picker v-model="filter.date_range" type="daterange" start-placeholder="Start Date"
                                end-placeholder="End Date" value-format="yyyy-MM-dd" size="small" style="width: 240px;">
                            </el-date-picker>
                        </div>
                        <div class="col-auto">
                            <span class="badge badge-primary rounded"><i class="fa fa-calendar mr-1"></i> Period: {{ periodLabel }}</span>
                        </div>
                    </div>
                </div>
                <div class="table-responsive" style="height: 470px; overflow-y: auto;">
                    <div v-if="!onLoad">
                        <table class="table">
                            <!-- Per MCR -->
                            <template v-if="filter.view_by == 'per-mcr'">
                                <thead>
                                    <tr>
                                        <th @click="sortTable('mcr_number')" style="cursor:pointer;">
                                            {{ tt('mcr_number') }} <i :class="sortIcon('mcr_number')"></i>
                                        </th>
                                        <th @click="sortTable('created_at')" style="cursor:pointer;">
                                            {{ tt('created_at') }} <i :class="sortIcon('created_at')"></i>
                                        </th>
                                        <th class="text-center" @click="sortTable('total_approval_actions')" style="cursor:pointer;">
                                            {{ tt('total_approval_actions') }} <i :class="sortIcon('total_approval_actions')"></i>
                                        </th>
                                        <th class="text-center" @click="sortTable('avg_duration')" style="cursor:pointer;">
                                            {{ tt('avg_duration') }} <i :class="sortIcon('avg_duration')"></i>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, index) in table.data" :key="index">
                                        <td>
                                            <a href="#" @click.prevent="goToMaterialRequest(row.mcr_number)">{{ row.mcr_number }}</a>
                                        </td>
                                        <td>{{ moment(row.created_at).locale('id').format('LLL') }}</td>
                                        <td class="text-center"><span class="badge badge-primary" style="font-size: 0.6rem;">{{ row.total_approval_actions }}</span></td>
                                        <td class="text-center">{{ row.avg_duration.days }} days {{ row.avg_duration.hours }} hours</td>
                                    </tr>
                                    <tr v-if="table.data.length == 0">
                                        <td colspan="4" class="text-center">{{ tt('no_data') }}</td>
                                    </tr>
                                </tbody>
                            </template>
                            <!-- Per Company -->
                            <template v-else-if="filter.view_by == 'per-company'">
                                <thead>
                                    <tr>
                                        <th @click="sortTable('company_code')" style="cursor:pointer;">
                                            {{ tt('company_code') }} <i :class="sortIcon('company_code')"></i>
                                        </th>
                                        <th @click="sortTable('company_description')" style="cursor:pointer;">
                                            {{ tt('company_description') }} <i
                                                :class="sortIcon('company_description')"></i>
                                        </th>
                                        <th class="text-center" @click="sortTable('total_mcr')" style="cursor:pointer;">
                                            {{ tt('total_mcr') }} <i :class="sortIcon('total_mcr')"></i>
                                        </th>
                                        <th class="text-center" @click="sortTable('total_approval_actions')"
                                            style="cursor:pointer;">
                                            {{ tt('total_approval_actions') }} <i
                                                :class="sortIcon('total_approval_actions')"></i>
                                        </th>
                                        <th class="text-center" @click="sortTable('avg_duration')"
                                            style="cursor:pointer;">
                                            {{ tt('avg_duration') }} <i :class="sortIcon('avg_duration')"></i>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, index) in table.data" :key="index">
                                        <td>{{ row.company_code }}</td>
                                        <td>{{ row.company_description }}</td>
                                        <td class="text-center"><span class="badge badge-primary" style="font-size: 0.6rem;">{{ row.total_mcr }}</span></td>
                                        <td class="text-center"><span class="badge badge-primary" style="font-size: 0.6rem;">{{ row.total_approval_actions }}</span></td>
                                        <td class="text-center">{{ row.avg_duration.days }} days {{ row.avg_duration.hours }} hours</td>
                                    </tr>
                                    <tr v-if="table.data.length == 0">
                                        <td colspan="5" class="text-center">{{ tt('no_data') }}</td>
                                    </tr>
                                </tbody>
                            </template>
                            <!-- Per Jabatan -->
                            <template v-else-if="filter.view_by == 'per-jabatan'">
                                <thead>
                                    <tr>
                                        <th @click="sortTable('step_name')" style="cursor:pointer;">
                                            {{ tt('role_name') }} <i :class="sortIcon('step_name')"></i>
                                        </th>
                                        <th @click="sortTable('company_code')" style="cursor:pointer;">
                                            {{ tt('company_code') }} <i :class="sortIcon('company_code')"></i>
                                        </th>
                                        <th @click="sortTable('company_description')" style="cursor:pointer;">
                                            {{ tt('company_description') }} <i
                                                :class="sortIcon('company_description')"></i>
                                        </th>
                                        <th class="text-center" @click="sortTable('total_mcr')" style="cursor:pointer;">
                                            {{ tt('total_mcr') }} <i :class="sortIcon('total_mcr')"></i>
                                        </th>
                                        <th class="text-center" @click="sortTable('total_approval_actions')"
                                            style="cursor:pointer;">
                                            {{ tt('total_approval_actions') }} <i
                                                :class="sortIcon('total_approval_actions')"></i>
                                        </th>
                                        <th class="text-center" @click="sortTable('avg_duration')"
                                            style="cursor:pointer;">
                                            {{ tt('avg_duration') }} <i :class="sortIcon('avg_duration')"></i>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, index) in table.data" :key="index">
                                        <td>{{ row.step_name }}</td>
                                        <td>{{ row.company_code }}</td>
                                        <td>{{ row.company_description }}</td>
                                        <td class="text-center"><span class="badge badge-primary" style="font-size: 0.6rem;">{{ row.total_mcr }}</span></td>
                                        <td class="text-center"><span class="badge badge-primary" style="font-size: 0.6rem;">{{ row.total_approval_actions }}</span></td>
                                        <td class="text-center">{{ row.avg_duration.days }} days {{
                                            row.avg_duration.hours }} hours</td>
                                    </tr>
                                    <tr v-if="table.data.length == 0">
                                        <td colspan="6" class="text-center">{{ tt('no_data') }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </table>
                    </div>
                    <skeleton-loading v-else />
                </div>
                <div class="card-footer pb-0" v-if="!onLoad">
                    <span class="float-left">
                        <base-pagination :page-count="table.page.last_page" v-model="table.page.current_page"
                            @click.native="changePage(table.page.current_page)" size="sm"></base-pagination>
                    </span>
                    <span class="float-right">
                        {{ tt('page_info', {
                            size: (table.page.current_page - 1) * table.page.per_page +
                                table.data.length, total:
                        table.total}) }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState } from 'vuex';
import Api from '@/helpers/api';
import mcrAvgDuration from '@/services/dashboard/mcrAvgDuration.service';
import companyCode from '@/services/master/companyCode.service';
import moment from 'moment';

export default {
    data() {
        const today = moment().format('YYYY-MM-DD');
        const oneYearAgo = moment().subtract(1, 'year').format('YYYY-MM-DD');
        return {
            moment: moment,
            onLoad: false,
            onExport: false,
            companyList: [],
            companyLoading: false,
            sort: {
                by: '',
                dir: 'desc',
            },
            filter: {
                view_by: 'per-mcr',
                mcr_number: '',
                mcr_type: '',
                company_code: '',
                role_name: '',
                date_range: [oneYearAgo, today],
            },
            table: {
                data: [],
                total: 0,
                page: {
                    current_page: 1,
                    last_page: 1,
                    per_page: 100,
                },
            },
        }
    },
    computed: {
        ...mapState({
            authUserPermission: state => state.auth.userPermission
        }),
        periodLabel() {
            if (Array.isArray(this.filter.date_range) && this.filter.date_range[0] && this.filter.date_range[1]) {
                return this.filter.date_range[0] + ' s/d ' + this.filter.date_range[1];
            }
            return '-';
        },
    },
    mounted() {
        this.getData();
    },
    methods: {
        downloadExport() {
            let context = this;
            context.onExport = true;
            const params = {
                view_by: context.filter.view_by,
                start_date: Array.isArray(context.filter.date_range) ? context.filter.date_range[0] : '',
                end_date: Array.isArray(context.filter.date_range) ? context.filter.date_range[1] : '',
                mcr_number: context.filter.mcr_number,
                company_code: context.filter.company_code,
                role_name: context.filter.role_name,
            };
            if (context.sort.by) {
                params['sort_by[]'] = context.sort.by;
                params['sort_dir[]'] = context.sort.dir;
            }
            mcrAvgDuration.export(params).then(function(response) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'MCR_Avg_Duration.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                context.onExport = false;
            }).catch(function() {
                context.onExport = false;
            });
        },
        onChangeViewBy() {
            this.filter.mcr_number = '';
            this.filter.mcr_type = '';
            this.filter.company_code = '';
            this.filter.role_name = '';
            this.sort.by = '';
            this.sort.dir = 'desc';
            this.table.data = [];
            this.table.total = 0;
            this.table.page.current_page = 1;
            this.getData();
        },
        sortTable(col) {
            if (this.sort.by === col) {
                this.sort.dir = this.sort.dir === 'asc' ? 'desc' : 'asc';
            } else {
                this.sort.by = col;
                this.sort.dir = 'desc';
            }
            this.table.page.current_page = 1;
            this.getData();
        },
        sortIcon(col) {
            if (this.sort.by !== col) return 'fa fa-sort text-muted';
            return this.sort.dir === 'asc' ? 'fa fa-sort-up' : 'fa fa-sort-down';
        },
        goToMaterialRequest(mcrNumber) {
            this.$router.push({ path: '/material/material-request', query: { mcr_number: mcrNumber } });
        },
        searchCompany(query) {
            let context = this;
            context.companyLoading = true;
            Api(context, companyCode.get(null, { per_page: 'none', search: query }))
                .onSuccess(function (response) {
                    context.companyList = response.data.data.data.data;
                    context.companyLoading = false;
                })
                .onError(function () {
                    context.companyList = [];
                    context.companyLoading = false;
                })
                .call();
        },
        changePage(page) {
            this.table.page.current_page = page;
            this.getData();
        },
        getData() {
            let context = this;
            context.onLoad = true;

            const params = {
                per_page: context.table.page.per_page,
                page: context.table.page.current_page,
                start_date: Array.isArray(context.filter.date_range) ? context.filter.date_range[0] : '',
                end_date: Array.isArray(context.filter.date_range) ? context.filter.date_range[1] : '',
            };

            if (context.sort.by) {
                params['sort_by[]'] = context.sort.by;
                params['sort_dir[]'] = context.sort.dir;
            }

            let serviceCall;
            if (context.filter.view_by == 'per-mcr') {
                params.mcr_number = context.filter.mcr_number;
                serviceCall = mcrAvgDuration.perMcr(params);
            } else if (context.filter.view_by == 'per-company') {
                params.company_code = context.filter.company_code;
                serviceCall = mcrAvgDuration.perCompany(params);
            } else {
                params.role_name = context.filter.role_name;
                params.company_code = context.filter.company_code;
                serviceCall = mcrAvgDuration.perJabatan(params);
            }

            Api(context, serviceCall)
                .onSuccess(function (response) {
                    context.table.data = response.data.data.data;
                    context.table.total = response.data.data.total;
                    context.table.page = response.data.data;
                    context.onLoad = false;
                })
                .onError(function () {
                    context.table.data = [];
                    context.table.total = 0;
                    context.onLoad = false;
                })
                .call();
        },
    }
};
</script>

<style scoped>
::v-deep .el-select .el-input .el-input__inner {
    font-size: 0.875rem;
    height: 32px !important;
    -webkit-transition: all 0.15s ease-in-out;
    transition: all 0.15s ease-in-out;
}
</style>
